'use client';

import { asText, Content } from '@prismicio/client';
import { faArrowRight, faHospitals } from '@fortawesome/pro-regular-svg-icons';

import {
  Box,
  Icon,
  Card,
  Avatar,
  Heading,
  Address,
  Typography,
  CardBody,
} from '@preventx/oxygen';
import { toShortDate } from '@preventx/prismic-services';

// Types
import { ISOLanguageKeys } from '@/types/language';
import { DataStructureResponse } from '@/types/siteSearch/mapper';

// Constants
import { DateReplacementKey } from '@/constants/configuration';

// Contexts
import { useSiteContext } from '@/context/SiteProvider';

// Components
import { Link } from '@/components/Actions/Link';

type ClinicsContentProps = {
  language: ISOLanguageKeys;
  content: DataStructureResponse[];
  onClick: (objectIDs: string[]) => void;
  containerContent: Content.SiteHeaderDocumentData;
};

export const ClinicsContent = ({
  content,
  onClick,
  language,
  containerContent,
}: ClinicsContentProps) => {
  const { reusableContent } = useSiteContext();

  const lastReviewed = asText(reusableContent?.last_reviewed) ?? '';

  return (
    <>
      <Heading component="h2" size="h4">
        {asText(containerContent.search_clinics_title)}
      </Heading>

      <Card component="div">
        <CardBody>
          <Box
            component="nav"
            className="order-lines"
            aria-label={asText(containerContent.search_clinics_results_title)}
          >
            {content.map(({ locales, objectID }) => {
              if (!locales) {
                return null;
              }

              const locale = locales[language];

              if (!locale) {
                return null;
              }

              const { url, title, address, lastUpdated } = locale;

              return (
                <Link
                  href={url}
                  type="link"
                  as="default"
                  retainSearchParams
                  className="line-item flex-nowrap"
                  onClick={() => onClick([objectID])}
                  key={`search-result-clinic-${objectID + 1}`}
                >
                  <Box addFlow component="div" className="line-item__details">
                    <Box addCluster component="div" gap="gap-lg">
                      <Box addCluster component="div" className="media-object">
                        <Avatar colour="green" icon={faHospitals} />

                        <Box component="div" addFlow>
                          <Box component="div">
                            <Heading component="h3">{title}</Heading>
                            <Typography component="small">
                              {lastReviewed.replace(
                                DateReplacementKey,
                                toShortDate(lastUpdated),
                              )}
                            </Typography>
                          </Box>

                          <Address
                            address={address ?? []}
                            id={`site-search-clinic-address-${objectID + 1}`}
                          />
                        </Box>
                      </Box>
                    </Box>
                  </Box>

                  <Box component="div" addCluster>
                    <Icon addDirectionalIcon icon={faArrowRight} />
                  </Box>
                </Link>
              );
            })}
          </Box>
        </CardBody>
      </Card>
    </>
  );
};
